/*
 * @Author: Yolo
 * @Date: 2024-04-24 10:06:05
 * @Last Modified by: Yolo
 * @Last Modified time: 2024-06-06 10:25:02
 * @Desc 坐标抽稀算法（正常使用效果还可以）
 */
import RBush from 'rbush'
import { GPS } from '@map-sdk/sdk/utils/GPS'
import type L from 'leaflet'

interface LatLng {
  lat: number
  lng: number
}

type CollisionResolutionStrategy =
  | 'nearest' // 选择与新点距离最近的已存在点
  | 'average' // 选择与新点距离平均值最小的已存在点集，并以这些点的平均位置作为新点
  | 'weightedAverage' // 选择与新点距离加权平均值最小的已存在点集，并以这些点的加权平均位置作为新点
  | 'merge' // 将新点与所有与其发生碰撞的点合并为一个新的点，位置取所有碰撞点的中心
  | 'discard' // 如果新点与已有点发生碰撞，忽略该新点，不添加到地图
  | 'custom' // 用户自定义的碰撞处理策略

interface PointClustererOptions {
  thresholdFactor: number // 默认抽稀阈值与缩放级别的比例
  maxPointRange: number // 默认点位最大覆盖范围（单位：像素）
  collisionResolution: CollisionResolutionStrategy // 默认碰撞处理策略（'nearest'：选择最近的已存在点）
  callback?: ((...params: any) => void) | null
  customCollisionResolver?: ((...params: any) => LatLng) | null
}

interface RequiredProperty {
  point: LatLng // 必须的属性
  [key: string]: any // 索引签名，允许任意其他属性
}

export class PointClusterer {
  private map: L.Map
  private originalPoints: LatLng[]
  private options: PointClustererOptions
  private rBush: RBush<{
    minX: number
    minY: number
    maxX: number
    maxY: number
    point: LatLng
  }>
  private pointData: RequiredProperty[]

  constructor(
    map: L.Map,
    originalPoints: RequiredProperty[],
    options: PointClustererOptions
  ) {
    this.map = map
    this.pointData = originalPoints
    this.originalPoints = originalPoints.map((item) => item.point)
    this.options = {
      thresholdFactor: options.thresholdFactor ?? 5, // 默认抽稀阈值与缩放级别的比例
      maxPointRange: options.maxPointRange ?? 80, // 默认点位最大覆盖范围（单位：像素）
      collisionResolution: options.collisionResolution ?? 'nearest', // 默认碰撞处理策略（'nearest'：选择最近的已存在点）
      callback: options.callback,
      customCollisionResolver: options.customCollisionResolver,
    }
    this.rBush = new RBush() // 初始化空间索引

    this.init()
  }

  private init(): void {
    this.map.on('zoomend', () => this.updatePoints())
    this.updatePoints()
  }

  private getProcessedData = (processedPoints: LatLng[]) => {
    return this.pointData.filter((item) => processedPoints.includes(item.point))
  }

  private updatePoints(): void {
    const currentZoom = this.map.getZoom()
    const threshold = this.calculateThreshold(currentZoom)
    // 获取当前地图边界 删选出相关数据
    const bounds = this.map.getBounds()
    const points = this.originalPoints.filter((item) => bounds.contains(item))
    const simplifiedPoints = this.douglasPeucker(points, threshold)
    const processedPoints = this.processPoints(
      simplifiedPoints,
      this.options.maxPointRange
    )

    this.options.callback &&
      this.options.callback(this.getProcessedData(processedPoints))
  }

  private calculateThreshold(zoomLevel: number): number {
    const maxZoom = this.map.getMaxZoom()
    return 2 ** (maxZoom - zoomLevel) * this.options.thresholdFactor
  }

  private douglasPeucker(points: LatLng[], threshold: number): LatLng[] {
    // 实现道格拉斯-普克算法，返回抽稀后的点集
    // 注意：此处需用 TypeScript 实现该算法，返回类型为 LatLng[]
    if (points.length < 3) {
      return points // 如果点数小于3，直接返回原点集
    }

    let dmax = 0
    let index = 0
    for (let i = 1; i < points.length - 1; i++) {
      const d = this.distanceToLineSegment(
        points[0],
        points[points.length - 1],
        points[i]
      )
      if (d > dmax) {
        index = i
        dmax = d
      }
    }

    if (dmax > threshold) {
      const recResults1 = this.douglasPeucker(
        points.slice(0, index + 1),
        threshold
      )
      const recResults2 = this.douglasPeucker(
        points.slice(index, points.length),
        threshold
      )
      return [...recResults1.slice(0, -1), ...recResults2]
    } else {
      return [points[0], points[points.length - 1]]
    }
  }

  private distanceToLineSegment(
    start: LatLng,
    end: LatLng,
    point: LatLng
  ): number {
    const dx = end.lng - start.lng
    const dy = end.lat - start.lat

    if (dx === 0 && dy === 0) {
      return this.distanceBetweenPoints(start, point)
    }

    const t =
      ((point.lng - start.lng) * dx + (point.lat - start.lat) * dy) /
      (dx * dx + dy * dy)

    if (t < 0) {
      return this.distanceBetweenPoints(start, point)
    } else if (t > 1) {
      return this.distanceBetweenPoints(end, point)
    } else {
      const projection = {
        lat: start.lat + t * dy,
        lng: start.lng + t * dx,
      }
      return this.distanceBetweenPoints(projection, point)
    }
  }
  // 欧氏距离公式
  // private distanceBetweenPoints(p1: LatLng, p2: LatLng): number {
  //   const dx = p2.lng - p1.lng
  //   const dy = p2.lat - p1.lat
  //   return Math.sqrt(dx * dx + dy * dy)
  // }
  //
  private distanceBetweenPoints(p1: LatLng, p2: LatLng): number {
    return GPS.distance(p1.lat, p1.lng, p2.lat, p2.lng)
  }
  //  Haversine 公式计算地球上两点之间的距离（以公里为单位）
  private processPoints(
    simplifiedPoints: LatLng[],
    maxPointRange: number
  ): LatLng[] {
    simplifiedPoints.forEach((point) => {
      // 判断当前点位是否与已处理点位发生碰撞
      const collidingPoints = this.rBush.search({
        minX: point.lng - maxPointRange,
        minY: point.lat - maxPointRange,
        maxX: point.lng + maxPointRange,
        maxY: point.lat + maxPointRange,
      })
      if (collidingPoints.length === 0) {
        // 无碰撞，添加到已处理点集并更新空间索引
        this.rBush.insert({
          minX: point.lng,
          minY: point.lat,
          maxX: point.lng,
          maxY: point.lat,
          point,
        })
      } else {
        // 发生碰撞，根据规则处理
        const resolvedPoint = this.resolveCollision(
          point,
          collidingPoints,
          this.options.collisionResolution
        )
        this.rBush.insert({
          minX: resolvedPoint.lng,
          minY: resolvedPoint.lat,
          maxX: resolvedPoint.lng,
          maxY: resolvedPoint.lat,
          point: resolvedPoint,
        })
      }
    })

    return this.rBush.all().map((entry) => entry.point)
  }

  private resolveCollision(
    newPoint: LatLng,
    collidingPoints: any[],
    strategy: CollisionResolutionStrategy
  ): LatLng {
    switch (strategy) {
      case 'nearest':
        return this.findNearest(collidingPoints, newPoint)
      case 'average':
        return this.resolveByAverage(newPoint, collidingPoints)
      case 'weightedAverage':
        return this.resolveByWeightedAverage(newPoint, collidingPoints)
      case 'merge':
        return this.resolveByMerge(newPoint, collidingPoints)
      case 'discard':
        return this.resolveByDiscard(newPoint)
      case 'custom':
        return this.resolveByCustom(newPoint, collidingPoints)
      default:
        throw new Error(
          `Unsupported collision resolution strategy: ${strategy}`
        )
    }
  }

  /**
   * 将新点与所有与其发生碰撞的点合并为一个新的点，位置取所有碰撞点的中心
   * @param newPoint
   * @param collidingPoints
   * @returns
   */
  private resolveByMerge(newPoint: LatLng, collidingPoints: LatLng[]): LatLng {
    const mergedPoint = {
      lat: 0,
      lng: 0,
    }

    for (const point of [newPoint, ...collidingPoints]) {
      mergedPoint.lat += point.lat
      mergedPoint.lng += point.lng
    }

    const count = collidingPoints.length + 1
    mergedPoint.lat /= count
    mergedPoint.lng /= count

    return mergedPoint
  }

  /**
   * 如果新点与已有点发生碰撞，忽略该新点，不添加到地图
   * @param newPoint
   * @param collidingPoints
   * @returns
   */
  private resolveByDiscard(newPoint: LatLng): LatLng {
    // 不做任何操作，直接返回新点，但在实际添加点到地图时会被忽略
    return newPoint
  }

  /**
   * 用户自定义的碰撞处理策略
   * @param newPoint
   * @param collidingPoints
   * @returns
   */
  private resolveByCustom(newPoint: LatLng, collidingPoints: LatLng[]): LatLng {
    if (typeof this.options.customCollisionResolver !== 'function') {
      throw new TypeError(
        '需要配置options 中 customCollisionResolver 参数 为回到函数模式'
      )
    }

    return this.options.customCollisionResolver(newPoint, collidingPoints)
  }

  /**
   * 选择与新点距离加权平均值最小的已存在点集，并以这些点的加权平均位置作为新点
   * @param newPoint
   * @param collidingPoints
   * @returns
   */
  private resolveByWeightedAverage(
    newPoint: LatLng,
    collidingPoints: LatLng[]
  ): LatLng {
    const weightFunction = (point: LatLng) =>
      1 / this.distanceBetweenPoints(newPoint, point) // 示例权重函数，距离越近权重越大
    const sumLat = collidingPoints.reduce(
      (sum, point) => sum + point.lat * weightFunction(point),
      newPoint.lat
    )
    const sumLng = collidingPoints.reduce(
      (sum, point) => sum + point.lng * weightFunction(point),
      newPoint.lng
    )
    const sumWeight = collidingPoints.reduce(
      (sum, point) => sum + weightFunction(point),
      1
    )

    return {
      lat: sumLat / sumWeight,
      lng: sumLng / sumWeight,
    }
  }

  /**
   * 选择与新点距离平均值最小的已存在点集，并以这些点的平均位置作为新点
   * @param newPoint
   * @param collidingPoints
   * @returns
   */
  private resolveByAverage(
    newPoint: LatLng,
    collidingPoints: LatLng[]
  ): LatLng {
    const sumLat = collidingPoints.reduce(
      (sum, point) => sum + point.lat,
      newPoint.lat
    )
    const sumLng = collidingPoints.reduce(
      (sum, point) => sum + point.lng,
      newPoint.lng
    )
    const count = collidingPoints.length + 1

    return {
      lat: sumLat / count,
      lng: sumLng / count,
    }
  }
  /**
   *  选择与新点距离最近的已存在点
   * @param collidingPoints
   * @param point
   * @returns
   */
  private findNearest(collidingPoints: LatLng[], point: LatLng): LatLng {
    // 实现寻找最近点的功能，返回类型为 LatLng
    let nearestPoint: LatLng | null = null
    let minDistance = Number.POSITIVE_INFINITY
    for (const candidate of collidingPoints) {
      const { point: rubPoint } = candidate as any
      const distance = this.distanceBetweenPoints(point, rubPoint)
      if (distance < minDistance) {
        minDistance = distance
        nearestPoint = rubPoint
      }
    }

    if (nearestPoint === null) {
      throw new Error('No colliding points found')
    }

    return nearestPoint
  }
}

// // 使用示例
// const map = L.map('map').setView([0, 0], ½);
// const originalPoints: LatLng[] = []; // 填充原始轨迹点数据
// const pointClusterer = new PointClusterer(map, originalPoints, {
//     thresholdFactor: 6,
//     maxPointRange: 100,
//     collisionResolution: 'nearest'
// });
